import { useState } from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Button from '@mui/material/Button';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { styled } from '@mui/material/styles';
import { Toolbar, ToolbarButton, useGridApiContext, useGridEvent } from '@mui/x-data-grid';

import { AttendanceValue } from 'api/hooks/private/periods/TakeAttendance';

import { useMenuControl } from 'hooks/useMenuControl';

import { ATTENDANCE_CONFIG_MAP } from '../cell-renderers/attendance';

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-start',
  gap: theme.spacing(1),

  // button height
  '& .MuiButton-root': {
    height: 36,
  },
}));

interface CoursePeriodRosterToolbarProps {
  handleStartAttendance: () => void;
  handleCancelAttendance: () => void;
  handleSaveAttendance: () => void;
  handleBulkEditAttendance: (value: AttendanceValue) => void;
}

declare module '@mui/x-data-grid' {
  interface ToolbarPropsOverrides extends CoursePeriodRosterToolbarProps {}
}

export function CoursePeriodRosterToolbar(props: CoursePeriodRosterToolbarProps) {
  const [isTakingAttendance, setIsTakingAttendance] = useState(false);
  const apiRef = useGridApiContext();
  const bulkEditAttendanceMenu = useMenuControl();

  // update state when the cell mode model changes
  useGridEvent(apiRef, 'cellModesModelChange', (params) => {
    setIsTakingAttendance(Object.values(params).some((value) => value['attendance'].mode === 'edit'));
  });

  // useGridEvent(apiRef, 'rowModesModelChange', (params) => {
  //   setIsTakingAttendance(Object.values(params).some((value) => value.mode === 'edit'));
  // });

  return (
    <StyledToolbar>
      {!isTakingAttendance && (
        <ToolbarButton render={<Button variant="outlined" />} onClick={props.handleStartAttendance}>
          Take Attendance
        </ToolbarButton>
      )}
      {isTakingAttendance && (
        <ToolbarButton render={<Button variant="outlined" color="error" />} onClick={props.handleCancelAttendance}>
          Cancel
        </ToolbarButton>
      )}
      {isTakingAttendance && (
        <ToolbarButton render={<Button variant="outlined" color="success" />} onClick={props.handleSaveAttendance}>
          Save
        </ToolbarButton>
      )}
      {isTakingAttendance && (
        <ToolbarButton
          render={
            <Button
              variant="outlined"
              endIcon={
                <KeyboardArrowDownIcon
                  fontSize="medium"
                  sx={{
                    transition: 'transform 0.3s ease',
                    transform: bulkEditAttendanceMenu.isOpen ? 'rotate(180deg)' : 'none',
                  }}
                />
              }
            />
          }
          onClick={bulkEditAttendanceMenu.openMenu}
        >
          Mark All
        </ToolbarButton>
      )}
      <Menu {...bulkEditAttendanceMenu.menuProps} onClick={bulkEditAttendanceMenu.closeMenu}>
        {Object.values(ATTENDANCE_CONFIG_MAP).map((config) => {
          const value = String(config.value);
          return (
            <MenuItem key={value} value={value} onClick={() => props.handleBulkEditAttendance(config.value)}>
              <ListItemIcon sx={{ minWidth: 36, color: config?.color }}>{config?.icon}</ListItemIcon>
              <ListItemText primary={config?.label ?? 'undefined'} sx={{ overflow: 'hidden', color: config?.color }} />
            </MenuItem>
          );
        })}
      </Menu>
    </StyledToolbar>
  );
}

export default CoursePeriodRosterToolbar;
